import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import UserContext from '../../context/User/User'
import AuthService from '../../utilities/services/auth.service'

import { getNewFeatures } from '../../utilities/api'

import { formatReleaseDate, resolveActionPath } from './whatsNewLogic'
import {
  dismissHint,
  markSlugsSeen,
  readWhatsNewState,
  unseenTourFeatures,
} from './whatsNewStorage'
import { FeatureModal } from './FeatureViews'
import WhatsNewFeed from './WhatsNewFeed'

import './whatsNew.scss'

const WhatsNewContext = React.createContext(null)

const useWhatsNew = () => {
  const value = useContext(WhatsNewContext)
  if (!value) {
    throw new Error('useWhatsNew must be used within WhatsNewProvider')
  }
  return value
}

const chronologicalTour = (releases, seenSlugs) => {
  const oldestFirst = [...(releases || [])].reverse().flatMap((release) =>
    [...(release.features || [])].reverse().map((feature) => ({
      ...feature,
      releasePublishedOn: release.publishedOn,
    }))
  )
  return unseenTourFeatures(oldestFirst, seenSlugs)
}

const WhatsNewProvider = ({ children }) => {
  const { authenticated } = useContext(UserContext)
  const location = useLocation()
  const navigate = useNavigate()
  const [releases, setReleases] = useState([])
  const [feedLoading, setFeedLoading] = useState(() => AuthService.isLoggedIn())
  const [seen, setSeen] = useState(() => readWhatsNewState())
  const [feedOpen, setFeedOpen] = useState(false)
  const [tourOpen, setTourOpen] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [replay, setReplay] = useState(null)
  const [imageIndex, setImageIndex] = useState(0)
  const [hintVisible, setHintVisible] = useState(false)
  const dismissedKey = useRef('')

  const load = useCallback(async () => {
    if (!AuthService.isLoggedIn()) {
      setReleases([])
      setFeedLoading(false)
      return
    }
    setFeedLoading(true)
    try {
      const response = await getNewFeatures()
      setReleases(response?.data?.data || [])
    } catch {
      setReleases([])
    } finally {
      setFeedLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
    const refresh = (event) => {
      const nextReleases = event?.detail?.releases
      if (Array.isArray(nextReleases)) {
        setReleases(nextReleases)
        setFeedLoading(false)
        return
      }
      load()
    }
    window.addEventListener('whats-new:refresh', refresh)
    return () => window.removeEventListener('whats-new:refresh', refresh)
  }, [authenticated, load])

  const tourSteps = useMemo(
    () => chronologicalTour(releases, seen.seenSlugs),
    [releases, seen.seenSlugs]
  )

  useEffect(() => {
    if (!AuthService.isLoggedIn()) return
    if (location.pathname === '/fulfillment') return
    if (!tourSteps.length) return
    const key = tourSteps.map((feature) => feature.slug).join('|')
    if (dismissedKey.current === key) return
    setStepIndex(0)
    setImageIndex(0)
    setTourOpen(true)
  }, [location.pathname, tourSteps])

  const closeTour = () => {
    const slugs = tourSteps.map((feature) => feature.slug)
    dismissedKey.current = slugs.join('|')
    const next = markSlugsSeen(slugs)
    setSeen(next)
    setTourOpen(false)
    if (!next.hintDismissed) setHintVisible(true)
  }

  const openFeed = () => {
    setFeedOpen(true)
    if (!hintVisible) return
    setHintVisible(false)
    setSeen(dismissHint())
  }

  const openReplay = (feature, startImage = 0) => {
    setReplay(feature)
    setImageIndex(startImage)
    setFeedOpen(true)
  }

  const editFeature = (feature) => {
    setReplay(null)
    setFeedOpen(false)
    navigate(`/dev/whats-new?feature=${encodeURIComponent(feature.slug)}`)
  }

  const goToFeature = (feature) => {
    const path = resolveActionPath(feature.actionPath, location.pathname)
    if (tourOpen) closeTour()
    setReplay(null)
    setFeedOpen(false)
    navigate(path)
  }

  const activeTourFeature = tourSteps[stepIndex] || null
  const tourDate = formatReleaseDate(
    activeTourFeature?.releasePublishedOn || releases[0]?.publishedOn
  )

  const value = {
    count: tourSteps.length,
    openFeed,
    hintVisible,
    dismissHint: () => {
      setHintVisible(false)
      setSeen(dismissHint())
    },
  }

  return (
    <WhatsNewContext.Provider value={value}>
      {children}
      <WhatsNewFeed
        show={feedOpen}
        loading={feedLoading}
        releases={releases}
        onHide={() => setFeedOpen(false)}
        onWatch={(feature) => openReplay(feature, 0)}
        onLook={(feature) => openReplay(feature, 0)}
        onAction={goToFeature}
        onEdit={editFeature}
      />
      <FeatureModal
        show={tourOpen && Boolean(activeTourFeature)}
        feature={activeTourFeature}
        mode="tour"
        imageIndex={imageIndex}
        onImageIndex={setImageIndex}
        stepIndex={stepIndex}
        stepCount={tourSteps.length}
        onStep={(index) => {
          setStepIndex(index)
          setImageIndex(0)
        }}
        stepLabel={`What's new · ${tourDate} · ${stepIndex + 1} of ${tourSteps.length}`}
        primaryLabel={stepIndex >= tourSteps.length - 1 ? 'Done' : 'Next'}
        onPrimary={() => {
          if (stepIndex >= tourSteps.length - 1) closeTour()
          else {
            setStepIndex((index) => index + 1)
            setImageIndex(0)
          }
        }}
        onBack={() => {
          setStepIndex((index) => Math.max(0, index - 1))
          setImageIndex(0)
        }}
        onAction={() => activeTourFeature && goToFeature(activeTourFeature)}
        onHide={closeTour}
      />
      <FeatureModal
        show={Boolean(replay)}
        feature={replay}
        mode="replay"
        imageIndex={imageIndex}
        onImageIndex={setImageIndex}
        stepLabel={`What's new · ${
          formatReleaseDate(
            releases.find((release) =>
              release.features?.some((feature) => feature.slug === replay?.slug)
            )?.publishedOn
          ) || tourDate
        } · Replay`}
        primaryLabel="Back to feed"
        onPrimary={() => setReplay(null)}
        onAction={() => replay && goToFeature(replay)}
        onHide={() => setReplay(null)}
      />
    </WhatsNewContext.Provider>
  )
}

export { useWhatsNew }
export default WhatsNewProvider
